import BCorpLogo from '@common/assets/images/bcorp-logo.svg';
import { MQ } from '@common/constants/mq.constant';
import { THEME } from '@common/constants/theme.constant';
import { EMPTY_LOQATE_ADDRESS, IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { Link } from '@common/general-ui/link/link.component';
import { Loading } from '@common/general-ui/loading/loading.component';
import { Trustpilot } from '@common/general-ui/trustpilot/trustpilot.component';
import { IUseCustomFormReturn, useForm } from '@common/hooks/use-form.hook';
import { ITabChangeEventHandler } from '@src/features/register/register-layout.component';
import { IRegisterReturn } from '@src/features/register/register.hook';
import { LINKS } from '@src/shared/links.constant';
import { Divider } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { css } from 'twin.macro';
import { AltRegisterLayoutV3 } from '../../register-layout.component';
import { AddressCheckerForm } from './address-checker-form.component';
import { PrismicImage } from '@common/general-ui/image/image.component';
import { useScreenMediaQuery } from '@common/hooks/use-screen-media-query.hook';

export interface IAddressFormProps {
    shippingAddressSearch: IInputLoqateValue;
}

interface IProps {
    isLoading: boolean;
    onAddressCheck: (address: IInputLoqateValue) => void;
    handleAddressChange: (address: IInputLoqateValue) => void;
    onFormUpdate: IRegisterReturn['saveToRegister'];
    onTabChange: ITabChangeEventHandler;
    registerInfo: IRegisterReturn['registerInfo'];
    saveToRegister: (data: Partial<IRegisterReturn['registerInfo']>) => void;
    showPostCodeNotServed: boolean;
    onFormInit: (form: IUseCustomFormReturn<IAddressFormProps>) => void;
}

export const CheckDeliveryComponent = ({
    isLoading,
    onAddressCheck,
    handleAddressChange,
    onFormUpdate,
    onTabChange,
    registerInfo,
    saveToRegister,
    showPostCodeNotServed,
    onFormInit,
}: IProps): JSX.Element => {
    const { isDownMD } = useScreenMediaQuery();
    const addressForm = useForm<IAddressFormProps>({
        enableReinitialize: false,
        defaultValues: {
            shippingAddressSearch: {
                city: registerInfo.shippingAddress.city,
                addressLine1: registerInfo.shippingAddress.addressLine1,
                addressLine2: registerInfo.shippingAddress.addressLine2,
                postCode: registerInfo.shippingAddress.postCode,
                addressLine3: registerInfo.shippingAddress.addressLine3,
                country: registerInfo.shippingAddress.country,
                label: registerInfo.shippingAddress.label || '',
                originalLabel: registerInfo.shippingAddress.originalLabel || '',
            },
        },
        onSubmit: useCallback(
            (data: IAddressFormProps) => {
                onAddressCheck(data.shippingAddressSearch);
            },
            [onAddressCheck],
        ),
        onFieldChange: useCallback(
            (_name, values: IAddressFormProps) => {
                onFormUpdate({ shippingAddress: values.shippingAddressSearch });
                if (values.shippingAddressSearch === EMPTY_LOQATE_ADDRESS) {
                    handleAddressChange(values.shippingAddressSearch);
                }
            },
            [handleAddressChange, onFormUpdate],
        ),
    });

    useEffect(() => {
        onFormInit(addressForm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onFormInit]);

    return (
        <Loading isLoading={isLoading}>
            <div css={styles.root}>
                <AltRegisterLayoutV3 onTabChange={onTabChange} registerInfo={registerInfo} saveToRegister={saveToRegister}>
                    <div css={styles.panelContainer} tw="grid grid-cols-10 lg:column-gap[16px] lg:auto-rows-min">
                        <div className="container" tw="col-span-10 lg:col-span-8 lg:col-start-2 lg:row-span-2 p-4 lg:p-6">
                            {isDownMD ? <PrismicImage tw='mb-4 w-full rounded-md' width={320} height={160} customParams="fit=crop" src={'https://images.prismic.io/oddbox/ZuhaYbVsGrYSvZ4i_Delivery_mobile.png?auto=format,compress'} alt='Oddbox box in front of a door.' /> :
                                <PrismicImage tw='mb-4 w-full' src={'https://images.prismic.io/oddbox/ZuLr-LVsGrYSvTAr_delivery.png?auto=format,compress'} alt='Oddbox box in front of a door.' />}

                            <h2 css={styles.header} tw="font-secondary uppercase font-normal mb-2">
                                Where would you like your boxes delivered?
                            </h2>
                            <p tw="font-light text-base leading-6 mb-4">
                                Delivery days vary by postcode to keep carbon emissions low.
                            </p>

                            <AddressCheckerForm
                                form={addressForm}
                                registerInfo={registerInfo}
                                showPostCodeNotServed={showPostCodeNotServed}
                            />
                            <p tw="text-sm font-light mt-4 sm:mt-11">
                                Already have an account? <br tw="hidden" />
                                <Link css={styles.link} tw="font-normal text-sm" name="signIn" href={LINKS.auth.login}>
                                    Sign in
                                </Link>
                            </p>

                            <Divider />

                            <div tw="flex justify-between items-center mt-4 lg:mt-6">
                                <div tw="p-1 rounded border">
                                    <img src={BCorpLogo} />
                                </div>
                                <div tw="sm:w-[420px] w-[180px]">
                                    <Trustpilot tw="w-full" template="microCombo" />
                                </div>
                            </div>
                        </div>
                    </div>
                </AltRegisterLayoutV3>
            </div>
        </Loading>
    );
};

const styles = {
    root: css`
        background: ${THEME.GRAY_200};
        display: flex;
        flex-direction: column;
        min-height: 100vh;
    `,
    panelContainer: css`
        width: 100%;
        flex: 1;
        max-width: 1300px;
        margin: 0 auto;
        ${MQ.lg} {
            padding: 0 0 40px;
        }
        .container {
            background: ${THEME.WHITE};
            border-radius: 6px;
        }
    `,
    header: css`
        font-size: 28px;
        line-height: 34px;
        ${MQ.lg} {
            font-size: 32px;
        }
    `,
    link: css`
        cursor: pointer;
        color: black;
        text-decoration: underline;
        text-decoration-thickness: 0.5px;
        &: hover {
            color: black;
            text-decoration-thickness: 0.5px !important;
        } ;
    `,
};
